import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Eye, Heart, Printer } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { api, errMessage } from "../lib/api";
import { THANK_YOU_NOTES, playChime, prefersReducedMotion } from "../lib/chime";

// In-app weekly recap preview (pilot). Members see only the children the family
// owner shared with them; owners see all consented children. No emails involved.
export const RecapPreview = () => {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [burst, setBurst] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/recap/preview");
      // Defensive: a payload without rows must never white-screen the dashboard.
      setPreview({ ...data, rows: data.rows ?? [] });
      // Thank-you moment: playful chime + floating hearts when a heart is
      // waiting — skipped entirely when the OS prefers reduced motion.
      if (!data.is_owner && data.thank_yous?.length > 0 && !prefersReducedMotion()) {
        playChime(THANK_YOU_NOTES);
        setBurst(true);
        setTimeout(() => setBurst(false), 2600);
      }
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-3" data-testid="recap-preview-panel">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Eye className="h-4 w-4 text-secondary" aria-hidden="true" />
          <p className="text-sm font-bold">This week's recap (in-app preview)</p>
        </div>
        <div className="flex items-center gap-2">
          <Button asChild variant="ghost" size="sm" className="rounded-lg h-8 gap-1.5" data-testid="recap-print-link">
            <Link to="/recap/print"><Printer className="h-3.5 w-3.5" aria-hidden="true" /> Keepsake</Link>
          </Button>
          <Button variant="outline" size="sm" className="rounded-lg h-8" onClick={load} disabled={loading}
            data-testid="recap-preview-button">
            {loading ? "Loading…" : preview ? "Refresh" : "Show preview"}
          </Button>
        </div>
      </div>
      {preview && (
        <div className="space-y-2" data-testid="recap-preview-content">
          {preview.rows.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="recap-preview-empty">
              {preview.is_owner
                ? "No recap data yet — it appears once a child with progress consent has played this week."
                : "Nothing shared with you yet. Ask the family owner to share a child's recap with you."}
            </p>
          ) : preview.rows.map((r) => (
            <div key={r.display_name} className="rounded-xl bg-accent px-4 py-3" data-testid={`recap-preview-row-${r.display_name}`}>
              <p className="text-sm font-bold">{r.display_name}</p>
              <p className="text-xs text-muted-foreground">
                {r.sessions} play sessions · {r.completed} activities · {r.stories} stories ·{" "}
                {r.words_practiced} words practiced · {r.stickers} stickers
                {r.favourite_story ? ` · favourite story: “${r.favourite_story}”` : ""}
              </p>
            </div>
          ))}
          {!preview.is_owner && preview.thank_yous?.length > 0 && (
            <div className="relative overflow-hidden rounded-xl border-2 border-secondary/40 bg-secondary/5 px-4 py-4 min-h-[76px] space-y-1"
              data-testid="member-thank-you-row">
              {burst && (
                <div className="pointer-events-none absolute inset-0" aria-hidden="true" data-testid="thank-you-hearts-burst">
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <Heart key={i} className="thankyou-heart-float absolute -bottom-1 h-4 w-4 text-secondary fill-current"
                      style={{ left: `${12 + i * 15}%`, animationDelay: `${i * 0.18}s` }} />
                  ))}
                </div>
              )}
              <p className="text-sm font-bold flex items-center gap-1.5">
                <Heart className={`h-4 w-4 text-secondary fill-current ${burst ? "animate-pulse" : ""}`} aria-hidden="true" />
                A thank-you heart for you!
              </p>
              {preview.thank_yous.map((t) => (
                <p key={t.child_id} className="text-xs text-muted-foreground" data-testid={`thank-you-${t.child_id}`}>
                  {t.child_name} sent the family a big heart back this week.
                </p>
              ))}
            </div>
          )}
          {!preview.is_owner && preview.rows.some((r) => r.child_id) && (
            <div className="rounded-xl border border-border px-4 py-3 space-y-2" data-testid="member-reactions-row">
              <p className="text-xs font-semibold text-muted-foreground">
                Send a cheer — the parent sees it on their dashboard (one per child each week).
              </p>
              {preview.rows.filter((r) => r.child_id).map((r) => (
                <div key={`react-${r.child_id}`} className="flex items-center justify-between gap-2 flex-wrap">
                  <span className="text-sm font-semibold">{r.display_name}</span>
                  <div className="flex gap-1.5">
                    {[["heart", "❤ Heart"], ["clap", "👏 Clap"], ["star", "⭐ Star"]].map(([key, label]) => (
                      <Button key={key} variant="outline" size="sm" className="rounded-full h-8 text-xs"
                        onClick={async () => {
                          try {
                            await api.post("/recap/reactions", { child_id: r.child_id, reaction_key: key });
                            toast.success(`Cheer sent for ${r.display_name}!`);
                          } catch (err) {
                            toast.error(errMessage(err));
                          }
                        }}
                        data-testid={`reaction-${key}-${r.child_id}`}>
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {!preview.is_owner && (
            <label className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-3 cursor-pointer" data-testid="member-email-row">
              <span className="text-sm">
                Email me this recap weekly
                <span className="block text-xs text-muted-foreground">One-click unsubscribe in every email.</span>
              </span>
              <Switch checked={preview.member_email_enabled}
                onCheckedChange={async (v) => {
                  try {
                    await api.put("/recap/member-email", { enabled: v });
                    setPreview({ ...preview, member_email_enabled: v });
                    toast.success(v ? "Weekly recap emails on" : "Weekly recap emails off");
                  } catch (err) {
                    toast.error(errMessage(err));
                  }
                }}
                data-testid="member-email-toggle" />
            </label>
          )}
        </div>
      )}
    </div>
  );
};
